import React, { useState } from 'react';
import { BoxCheckIcon, ClipboardIcon, FileLinesIcon, PlusIcon } from '@revfluence/fresh-icons/solid/esm';
import { FilterDrawer } from '@frontend/app/containers/ProductFulfillmentDashboard/FilterDrawer';
import { cn } from '@frontend/shadcn/lib/utils';
import { Card } from '@/shadcn/components/ui/card';
import { AmplifyTemplatePage, getPageImage, getPageName, PulseTemplatePage, useRecruitment } from '../RecruitmentContext';
import { SelectableCardGroup } from '../../components/SelectableCardGroup';

interface PageCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  className: string;
}
const PageCard = ({ title, description, icon, className }: PageCardProps) => {
  return (
    <div className="self-stretch bg-white inline-flex flex-col justify-center items-start gap-2">
      <div className="self-stretch inline-flex justify-start items-start gap-4">
        <div className={cn('w-8 h-8 rounded-lg flex justify-center items-center text-white', className)}>
          {icon}
        </div>
        <div className="flex-1 inline-flex flex-col justify-start items-start">
          <div className="self-stretch inline-flex justify-start items-center">
            <div className="flex-1 justify-start text-gray-800 text-base font-medium font-['Inter'] leading-7">{title}</div>
          </div>
          <div className="self-stretch flex flex-col justify-center items-start gap-1">
            <div className="self-stretch justify-start text-gray-500 text-sm font-normal font-['Inter'] leading-normal">{description}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Pages = () => {
  const { amplifyTemplate, pulseTemplate, selectedPage, selectPage, addBriefPage } = useRecruitment();

  const [open, setOpen] = useState(false);

  const [newPageType, setNewPageType] = useState<'brief' | 'product' | 'form'>('brief');

  const pages = amplifyTemplate?.pages || pulseTemplate?.pages;

  const handleAddPage = () => {
    if (newPageType === 'brief') {
      addBriefPage();
    }
    setOpen(false);
  };

  if (!pages) {
    return null;
  }

  return (
    <div className="w-[calc(100%+48px)] px-6 pt-2 pb-4 bg-grey-3 inline-flex flex-col justify-center items-center gap-4 rounded-b-2xl -mx-6 -mb-4">
      <div className="w-40 h-2 bg-grey-2 rounded-full" />
      <div className="inline-flex justify-start items-start gap-4">
        {pages.map((page: PulseTemplatePage | AmplifyTemplatePage, index: number) => (
          <div className="flex flex-col gap-1" key={index}>
            <Card
              className={`w-24 h-24 flex items-center justify-center border-border cursor-pointer ${selectedPage === index ? 'border-primary ring-2 ring-primary' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => selectPage(index)}
            >
              <img src={getPageImage(page)} alt={getPageName(page)} className="h-20 w-auto" />
            </Card>
            <div className="text-center justify-start text-foreground text-xs font-medium font-inter leading-tight p-1">
              {getPageName(page)}
            </div>
          </div>
        ))}

        <div className="flex flex-col gap-1">
          <Card
            className="w-24 h-24 flex items-center justify-center border-border border-dashed cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => setOpen(true)}
          >
            <PlusIcon fontSize={24} className="text-foreground" />
          </Card>
          <div className="text-center justify-start text-foreground text-xs font-medium font-inter leading-tight p-1">
            Add Page
          </div>
        </div>
      </div>
      <FilterDrawer
        open={open}
        onOpenChange={setOpen}
        headerProps={{
          title: 'Add Page',
        }}
        footerButtons={[
          { name: 'Add Page', onClick: handleAddPage, variant: 'primary' as const },
          { name: 'Cancel', onClick: () => setOpen(false), variant: 'outline' as const },
        ]}
        width="576px"
      >
        <div className="p-6 flex flex-col gap-4">
          <div className="self-stretch justify-start text-primaryMuted text-sm font-normal leading-normal">Select pages to add to your application. Additional pages enable you to gather essential details upfront, helping you review applications more efficiently and begin collaborating sooner.</div>
          <div className="pr-[1px]">
            <SelectableCardGroup<string>
              orientation="vertical"
              value={newPageType}
              options={[
                {
                  id: 'brief',
                  value: 'brief',
                  renderBody: (
                    <PageCard
                      title="Brief"
                      description="Enable creators to review and agree to your campaign brief before they are accepted to collaborate."
                      icon={<FileLinesIcon />}
                      className="bg-blue-5"
                    />
                  ),
                },
                {
                  id: 'product',
                  value: 'product',
                  renderBody: (
                    <PageCard
                      title="Products"
                      description="Surface your product catalog to get products to creators faster, review requests immediately after approval, and speed up turnaround time."
                      icon={<BoxCheckIcon />}
                      className="bg-yellow-5"
                    />
                  ),
                },
                {
                  id: 'form',
                  value: 'form',
                  renderBody: (
                    <PageCard
                      title="Form"
                      description="Add a form to request additional information from creators and comprehensively review applicants."
                      icon={<ClipboardIcon />}
                      className="bg-purple-5"
                    />
                  ),
                },
              ]}
              onChange={(value) => setNewPageType(value as 'brief' | 'product' | 'form')}
            />
          </div>
        </div>
      </FilterDrawer>
    </div>
  );
};
